import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AuthTokens, AuthUser } from './types';

const TOKEN_KEY = 'forms.auth.tokens';

function loadTokens(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthTokens;
  } catch {
    return null;
  }
}

function persistTokens(tokens: AuthTokens | null): void {
  try {
    if (tokens) localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export const useAuthStore = defineStore('auth', () => {
  const tokens = ref<AuthTokens | null>(loadTokens());
  const user = ref<AuthUser | null>(null);

  const isAuthenticated = computed(() => Boolean(tokens.value?.access_token && user.value));
  const accessToken = computed(() => tokens.value?.access_token ?? null);
  const refreshToken = computed(() => tokens.value?.refresh_token ?? null);
  const isAdmin = computed(() => Boolean(user.value?.is_admin));
  const isSuperAdmin = computed(() => Boolean(user.value?.is_super_admin));

  function setTokens(next: AuthTokens | null): void {
    tokens.value = next;
    persistTokens(next);
  }

  function setUser(next: AuthUser | null): void {
    user.value = next;
  }

  function clear(): void {
    tokens.value = null;
    user.value = null;
    persistTokens(null);
  }

  function can(_slugs: string | string[], _requireAll = false): boolean {
    return isAuthenticated.value;
  }

  return {
    tokens,
    user,
    isAuthenticated,
    accessToken,
    refreshToken,
    isAdmin,
    isSuperAdmin,
    setTokens,
    setUser,
    clear,
    can,
  };
});
